import { Centrifuge, Subscription, PublicationContext } from 'centrifuge'

export type WsMessageCallback = (data: unknown) => void

interface WsConfig {
    server: string
    token: string
    channel_prefix: string
    site_id: number
}

/**
 * SpaceSocket — singleton WebSocket service for GonkaGame SPA.
 * Wraps Centrifuge client and manages channel subscriptions.
 *
 * Usage:
 *   import { spaceSocket } from '@/services/spaceSocket'
 *   spaceSocket.init(profile.ws)
 *   spaceSocket.subscribe('global', (data) => { ... })
 */
class SpaceSocket {
    private centrifuge: Centrifuge | null = null
    private subscriptions = new Map<string, Subscription>()
    private config: WsConfig | null = null

    /**
     * Initialize the Centrifuge connection.
     * Call once after receiving ws config from the profile API.
     */
    init(config: WsConfig): void {
        if (this.centrifuge) {
            return // already initialized
        }

        this.config = config

        const protocol = window.location.protocol === 'https:' ? 'wss' : 'ws'
        const wsUrl = `${protocol}://${config.server}/connection/websocket`

        this.centrifuge = new Centrifuge(wsUrl, {
            token: config.token,
        })

        this.centrifuge.on('connecting', (ctx) => {
            console.debug('[SpaceSocket] connecting:', ctx.code, ctx.reason)
        })

        this.centrifuge.on('connected', (ctx) => {
            console.debug('[SpaceSocket] connected:', ctx.client)
        })

        this.centrifuge.on('disconnected', (ctx) => {
            console.debug('[SpaceSocket] disconnected:', ctx.code, ctx.reason)
        })

        this.centrifuge.connect()
    }

    /**
     * Subscribe to a named channel.
     * Automatically prepends the channel prefix: "{prefix}:{siteId}-{channel}"
     *
     * @param channel - Short channel name (e.g. 'global', 'payment:uuid')
     * @param callback - Called when a message is published to the channel
     */
    subscribe(channel: string, callback?: WsMessageCallback): Subscription | null {
        if (!this.centrifuge || !this.config) {
            console.warn('[SpaceSocket] not initialized, call init() first')
            return null
        }

        const fullChannel = `${this.config.channel_prefix}:${this.config.site_id}-${channel}`

        // Don't duplicate subscriptions
        if (this.subscriptions.has(fullChannel)) {
            return this.subscriptions.get(fullChannel)!
        }

        const sub = this.centrifuge.newSubscription(fullChannel)

        if (callback) {
            sub.on('publication', (ctx: PublicationContext) => {
                callback(ctx.data)
            })
        }

        sub.on('subscribing', (ctx) => {
            console.debug(`[SpaceSocket] subscribing to ${channel}:`, ctx.code, ctx.reason)
        })

        sub.on('subscribed', (ctx) => {
            console.debug(`[SpaceSocket] subscribed to ${channel}:`, ctx.channel)
        })

        sub.on('unsubscribed', (ctx) => {
            console.debug(`[SpaceSocket] unsubscribed from ${channel}:`, ctx.code, ctx.reason)
        })

        sub.subscribe()
        this.subscriptions.set(fullChannel, sub)

        return sub
    }

    /**
     * Unsubscribe from a specific channel.
     */
    unsubscribe(channel: string): void {
        if (!this.config) return

        const fullChannel = `${this.config.channel_prefix}:${this.config.site_id}-${channel}`
        const sub = this.subscriptions.get(fullChannel)
        if (sub) {
            sub.unsubscribe()
            sub.removeAllListeners()
            this.centrifuge?.removeSubscription(sub)
            this.subscriptions.delete(fullChannel)
        }
    }

    /**
     * Disconnect the client and clean up all subscriptions.
     * Call on user logout.
     */
    disconnect(): void {
        this.subscriptions.forEach((sub) => {
            sub.unsubscribe()
            sub.removeAllListeners()
        })
        this.subscriptions.clear()

        if (this.centrifuge) {
            this.centrifuge.disconnect()
            this.centrifuge = null
        }

        this.config = null
    }

    /**
     * Whether the socket is currently connected.
     */
    get isConnected(): boolean {
        return this.centrifuge?.state === 'connected'
    }
}

/** Singleton instance */
export const spaceSocket = new SpaceSocket()
